/**
 * Step 3: starting-point selection.
 *
 * Three clear paths:
 *   1. Reception — choose one of four opinionated generation styles
 *   2. Ceremony  — configure chair count and optional arbor
 *   3. Blank     — empty room, coordinator builds everything manually
 *
 * The ceremony sub-form appears inline (no extra wizard step) to keep
 * the 4-step flow intact. It collects only what the generation engine
 * genuinely needs: chair count and arbor preference. Room is already
 * known from Step 1.
 *
 * Auto-advance timing:
 *   - Reception style selected → 900ms pause (coordinator reads the
 *     style description), then advance
 *   - Blank selected → immediate advance (no description to read)
 *   - Ceremony → coordinator completes the sub-form and clicks Continue
 */

import { useEffect, useRef, useState } from 'react';
import type { LayoutStyle } from '../../domain/types';
import type { WizardData } from './NewWeddingWizard';

interface StepStyleSelectProps {
  data: WizardData;
  onChange: (changes: Partial<WizardData>) => void;
  onNext: () => void;
}

const RECEPTION_STYLES: { value: LayoutStyle; name: string; description: string }[] = [
  {
    value: 'classic_banquet',
    name: 'Classic Banquet',
    description: 'Round tables in a classic, elegant arrangement \u2014 our most popular choice.',
  },
  {
    value: 'long_table_feast',
    name: 'Long Table Feast',
    description: 'Long communal rows for a family-style feel. Best for smaller, intimate guest counts.',
  },
  {
    value: 'mixed_modern',
    name: 'Mixed Modern',
    description: 'Round guest tables with a striking rectangular head table.',
  },
  {
    value: 'lounge_cocktail',
    name: 'Lounge & Cocktail',
    description: 'A mingling-focused reception with fewer seated tables and cocktail rounds.',
  },
];

const AUTO_ADVANCE_DELAY_MS = 900;

// Which top-level path is expanded
type TopPath = 'none' | 'reception' | 'ceremony' | 'blank';

export function StepStyleSelect({ data, onChange, onNext }: StepStyleSelectProps) {
  const selectedReceptionStyle = RECEPTION_STYLES.find((s) => s.value === data.layoutStyle);
  const advanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Local mirror of ceremonyChairCount for the controlled input so we can
  // show the current value as a string while the field is being typed into.
  const [chairCountStr, setChairCountStr] = useState(
    String(data.ceremonyChairCount > 0 ? data.ceremonyChairCount : data.guestCount)
  );

  // Tracks whether the Reception card header has been clicked to expand the
  // style sub-grid. Separate from data.layoutStyle (which is only set once a
  // specific style is chosen) so the sub-grid can appear as soon as the
  // coordinator taps "Reception", before they've picked Classic Banquet etc.
  const [receptionExpanded, setReceptionExpanded] = useState(false);

  // Derived from both data (ceremony/blank flags, chosen style) and local
  // state (whether the reception header has been clicked to expand the sub-grid).
  // Must sit after all useState calls so receptionExpanded is in scope.
  const activePath: TopPath = data.startBlank
    ? 'blank'
    : data.startCeremony
      ? 'ceremony'
      : (receptionExpanded || !!data.layoutStyle)
        ? 'reception'
        : 'none';

  useEffect(() => {
    return () => {
      if (advanceTimeoutRef.current) clearTimeout(advanceTimeoutRef.current);
    };
  }, []);

  // -----------------------------------------------------------------------
  // Reception
  // -----------------------------------------------------------------------
  function handleSelectReceptionTop() {
    // Expand the reception panel but don't advance yet — the coordinator
    // still needs to pick one of the four styles before we can continue.
    setReceptionExpanded(true);
    onChange({ startBlank: false, startCeremony: false });
    if (advanceTimeoutRef.current) clearTimeout(advanceTimeoutRef.current);
  }

  function handleSelectStyle(value: LayoutStyle) {
    onChange({ layoutStyle: value, startBlank: false, startCeremony: false });
    if (advanceTimeoutRef.current) clearTimeout(advanceTimeoutRef.current);
    advanceTimeoutRef.current = setTimeout(onNext, AUTO_ADVANCE_DELAY_MS);
  }

  // -----------------------------------------------------------------------
  // Ceremony
  // -----------------------------------------------------------------------
  function handleSelectCeremonyTop() {
    // Set ceremony mode and sync ceremonyChairCount from guestCount
    setReceptionExpanded(false);
    const initialCount = data.guestCount > 0 ? data.guestCount : 50;
    setChairCountStr(String(initialCount));
    onChange({
      startCeremony: true,
      startBlank: false,
      layoutStyle: null,
      ceremonyChairCount: initialCount,
      ceremonyIncludeArbor: true,
    });
    if (advanceTimeoutRef.current) clearTimeout(advanceTimeoutRef.current);
  }

  function handleChairCountChange(raw: string) {
    setChairCountStr(raw);
    const n = parseInt(raw, 10);
    if (!isNaN(n) && n > 0) {
      onChange({ ceremonyChairCount: n });
    }
  }

  function handleCeremonyArborChange(checked: boolean) {
    onChange({ ceremonyIncludeArbor: checked });
  }

  // -----------------------------------------------------------------------
  // Blank
  // -----------------------------------------------------------------------
  function handleSelectBlank() {
    setReceptionExpanded(false);
    onChange({ startBlank: true, startCeremony: false, layoutStyle: null });
    if (advanceTimeoutRef.current) clearTimeout(advanceTimeoutRef.current);
    onNext();
  }

  // -----------------------------------------------------------------------
  // Can the coordinator continue?
  // -----------------------------------------------------------------------
  const canContinue =
    data.startBlank ||
    (data.startCeremony && data.ceremonyChairCount > 0) ||
    (!data.startCeremony && !data.startBlank && data.layoutStyle !== null);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <div>
      <h2 className="text-center font-display text-2xl text-ink-700">How would you like to start?</h2>

      <div className="mt-8 space-y-3">

        {/* ——— RECEPTION ——— */}
        <div
          className={
            activePath === 'reception'
              ? 'rounded-xl border-2 border-gold-400 bg-gold-50'
              : 'rounded-xl border-2 border-ivory-300 bg-white'
          }
        >
          <button
            onClick={handleSelectReceptionTop}
            className="flex w-full items-center gap-3 px-4 py-3 text-left"
          >
            {/* Icon placeholder */}
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-evergreen-100 text-lg">
              🍽️
            </span>
            <div>
              <p className="font-body text-sm font-semibold text-ink-700">Reception</p>
              <p className="font-body text-xs text-ink-400">
                Generate tables, dance floor, and feature zones for a seated reception.
              </p>
            </div>
          </button>

          {/* Reception style sub-grid — shown when Reception is active */}
          {activePath === 'reception' && (
            <div className="border-t border-gold-200 px-4 pb-4 pt-3">
              <p className="mb-3 font-body text-xs uppercase tracking-wide text-ink-400">
                Choose a style
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {RECEPTION_STYLES.map((style) => {
                  const selected = data.layoutStyle === style.value;
                  return (
                    <button
                      key={style.value}
                      onClick={() => handleSelectStyle(style.value)}
                      className={
                        selected
                          ? 'rounded-lg border-2 border-gold-500 bg-gold-100 p-3 text-center'
                          : 'rounded-lg border-2 border-ivory-300 bg-white p-3 text-center hover:border-gold-300'
                      }
                    >
                      <div className="mx-auto mb-2 h-12 w-full rounded bg-evergreen-100" aria-hidden />
                      <p className="font-body text-xs font-medium text-ink-700">{style.name}</p>
                    </button>
                  );
                })}
              </div>
              {selectedReceptionStyle && (
                <p className="mt-3 font-body text-xs text-ink-500">
                  &ldquo;{selectedReceptionStyle.description}&rdquo;
                </p>
              )}
            </div>
          )}
        </div>

        {/* ——— CEREMONY ——— */}
        <div
          className={
            activePath === 'ceremony'
              ? 'rounded-xl border-2 border-gold-400 bg-gold-50'
              : 'rounded-xl border-2 border-ivory-300 bg-white'
          }
        >
          <button
            onClick={handleSelectCeremonyTop}
            className="flex w-full items-center gap-3 px-4 py-3 text-left"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-evergreen-100 text-lg">
              💐
            </span>
            <div>
              <p className="font-body text-sm font-semibold text-ink-700">Ceremony</p>
              <p className="font-body text-xs text-ink-400">
                Generate chair rows with a center aisle, plus an optional arbor.
              </p>
            </div>
          </button>

          {/* Ceremony sub-form — shown when Ceremony is active */}
          {activePath === 'ceremony' && (
            <div className="border-t border-gold-200 px-4 pb-4 pt-3">
              <div className="space-y-3">
                <label className="block">
                  <span className="font-body text-sm font-medium text-ink-600">
                    Number of chairs (seats)
                  </span>
                  <p className="mb-1 font-body text-xs text-ink-400">
                    Chairs are placed in rows, divided evenly left and right of the aisle.
                  </p>
                  <input
                    type="number"
                    min={2}
                    value={chairCountStr}
                    onChange={(e) => handleChairCountChange(e.target.value)}
                    className="mt-1 w-full rounded border border-ivory-300 bg-white px-3 py-2 font-body text-sm text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200"
                  />
                </label>

                <label className="flex items-center gap-3 rounded-lg border border-ivory-300 bg-white px-4 py-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.ceremonyIncludeArbor}
                    onChange={(e) => handleCeremonyArborChange(e.target.checked)}
                    className="h-4 w-4 rounded border-ivory-300 accent-evergreen-600"
                  />
                  <div>
                    <p className="font-body text-sm font-medium text-ink-700">Include arbor</p>
                    <p className="font-body text-xs text-ink-400">
                      Places a moveable arbor at the ceremony focal end. You can reposition or remove it.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* ——— BLANK ——— */}
        <button
          onClick={handleSelectBlank}
          className={
            activePath === 'blank'
              ? 'flex w-full items-center gap-3 rounded-xl border-2 border-gold-400 bg-gold-50 px-4 py-3 text-left'
              : 'flex w-full items-center gap-3 rounded-xl border-2 border-ivory-300 bg-white px-4 py-3 text-left hover:border-gold-300'
          }
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ivory-200 text-lg">
            ✏️
          </span>
          <div>
            <p className="font-body text-sm font-semibold text-ink-700">Start Blank</p>
            <p className="font-body text-xs text-ink-400">
              Open an empty room and add everything yourself, exactly where you want it.
            </p>
          </div>
        </button>
      </div>

      <button
        onClick={onNext}
        disabled={!canContinue}
        className="mt-8 w-full rounded bg-evergreen-600 px-4 py-3 font-body font-medium text-ivory-50 hover:bg-evergreen-700 disabled:opacity-40"
      >
        Continue
      </button>
    </div>
  );
}
